import {
  action,
  atom,
  onConnect,
  onUpdate,
  reatomAsync,
  withAbort,
} from "@reatom/framework";
import { ProductControllerSearchSortByEnum } from "definitions/product-storage";
import { API } from "src/api";
import { withDebounce } from "src/common/pipes";

import { toCreatedAtRange } from "../../utils";
import {
  filterAtom,
  onFiltersChange,
  selectionIdAtom,
  sortAtom,
} from "../atoms";

export const productsCountAtom = atom<number | null>(null, "productsCountAtom");

export const searchProductCountFetcher = reatomAsync(async (ctx) => {
  const filterData = ctx.get(filterAtom);
  const sortData = ctx.get(sortAtom);
  const selectionId = ctx.get(selectionIdAtom);

  const response = await API.productStorageApi
    .productControllerSearchCount(
      {
        query: filterData.search || undefined,
        categories: filterData.categoryIds?.map(Number),
        brands: filterData.brandIds?.map(Number),
        priceMin: filterData.priceFrom,
        priceMax: filterData.priceTo,
        ...toCreatedAtRange(filterData),
        currency: filterData.currency ?? "RUB",
        genders: filterData.genders,
        sources: filterData.sources,
        includeCustom: filterData.includeCustom,
        sortBy: sortData?.type ?? ProductControllerSearchSortByEnum.Popularity,
        ...(filterData.viewAllSelected && { selectionId }),
        excludeSelectionIds: filterData.excludeSamples?.map(Number),
      },
      {
        signal: ctx.controller.signal,
      },
    )
    .then((res) => res.data);

  return response.count;
}, "searchProductCountFetcher").pipe(withAbort());

const fetchProductsCount = action(async (ctx) => {
  const count = await ctx.schedule(() => searchProductCountFetcher(ctx));
  if (count !== undefined) {
    productsCountAtom(ctx, count);
  }
}, "fetchProductsCount");

const debouncedFetchProductsCount = fetchProductsCount.pipe(withDebounce(400));

onFiltersChange((ctx) => {
  debouncedFetchProductsCount(ctx).catch(() => null);
});
onUpdate(sortAtom, (ctx) => {
  debouncedFetchProductsCount(ctx).catch(() => null);
});
onUpdate(selectionIdAtom, (ctx) => {
  if (ctx.get(filterAtom).viewAllSelected) {
    debouncedFetchProductsCount(ctx).catch(() => null);
  }
});

onConnect(productsCountAtom, (ctx) => {
  if (ctx.get(productsCountAtom) === null) {
    fetchProductsCount(ctx).catch(() => null);
  }
});

export const isProductsCountLoading = atom(
  (ctx) => ctx.spy(searchProductCountFetcher.pendingAtom) > 0,
  "isProductsCountLoading",
);

export const searchProductCountStore = {
  productsCountAtom,
  searchProductCountFetcher,
  fetchProductsCount,
  isProductsCountLoading,
};
